import { LoadingSpinner } from '../../../components/LoadingSpinner';

export type DocumentUploadProgress = {
  completed: number;
  total: number;
};

export function DocumentUploadProgressOverlay({
  progress,
  compact,
}: {
  progress: DocumentUploadProgress | null;
  compact?: boolean;
}) {
  const total = progress?.total ?? 1;
  const completed = progress?.completed ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const label =
    total > 1
      ? `${completed} von ${total} Dateien hochgeladen…`
      : 'Datei wird hochgeladen…';

  return (
    <div
      className={`absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-app-surface/90 backdrop-blur-[2px] ${
        compact ? 'rounded-lg' : ''
      }`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className={`rounded-full bg-sky-100 flex items-center justify-center ${compact ? 'w-10 h-10' : 'w-14 h-14'}`}>
        <LoadingSpinner size={compact ? 'lg' : '2xl'} className="text-sky-500" />
      </div>
      <p className={`mt-3 font-medium text-sky-700 text-center px-4 ${compact ? 'text-xs' : 'text-sm'}`}>{label}</p>
      {total > 1 ? (
        <div className={`mt-3 rounded-full bg-sky-100 overflow-hidden ${compact ? 'w-36 h-1' : 'w-52 h-1.5'}`}>
          <div
            className="h-full bg-sky-500 transition-all duration-300 ease-out rounded-full"
            style={{ width: `${percent}%` }}
          />
        </div>
      ) : (
        <div className={`mt-3 rounded-full bg-sky-100 overflow-hidden ${compact ? 'w-36 h-1' : 'w-52 h-1.5'}`}>
          <div className="h-full w-full bg-sky-400/70 animate-pulse rounded-full" />
        </div>
      )}
    </div>
  );
}

export function DocumentUploadProgressBanner({
  progress,
}: {
  progress: DocumentUploadProgress | null;
}) {
  const total = progress?.total ?? 1;
  const completed = progress?.completed ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const label =
    total > 1
      ? `${completed} von ${total} Dateien werden hochgeladen…`
      : 'Datei wird hochgeladen…';

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 shadow-sm"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <LoadingSpinner size="lg" className="text-sky-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-sky-800 truncate">{label}</p>
        <div className="mt-1.5 h-1 rounded-full bg-sky-200 overflow-hidden">
          <div
            className={`h-full bg-sky-500 rounded-full ${total > 1 ? 'transition-all duration-300 ease-out' : 'w-full bg-sky-400/70 animate-pulse'}`}
            style={total > 1 ? { width: `${percent}%` } : undefined}
          />
        </div>
      </div>
    </div>
  );
}
